import { jsxs, jsx } from "react/jsx-runtime";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useColors, useAlert, useResponsive } from "@inbody/core";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PermContactCalendarOutlinedIcon from "@mui/icons-material/PermContactCalendarOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { EMPTY_VALUE } from "../constants.js";
import { useProfileImage } from "../hooks/useProfileImage.js";
import { OrgBreadcrumb } from "./OrgBreadcrumb.js";
import { SectionHeading } from "./SectionHeading.js";
import { UserAvatar } from "./UserAvatar.js";
import { MemberSeatCard } from "../../seat-location/MemberSeatCard.js";
function UserSearchDetail(props) {
  const { user, getOrgGroupHref, onSelectOrgGroup } = props;
  const { t } = useTranslation("ui");
  const { gray } = useColors();
  const { toastAlert } = useAlert();
  const { isMobile } = useResponsive();
  const { imageUrl } = useProfileImage(user == null ? void 0 : user.email);
  const handleCopy = useCallback(
    (text) => {
      if (!text) return;
      navigator.clipboard.writeText(text);
      toastAlert.success(t("클립보드에 복사되었습니다."));
    },
    [t, toastAlert]
  );
  if (!user) {
    return /* @__PURE__ */ jsxs(
      Stack,
      {
        flex: "1 1 auto",
        alignItems: "center",
        justifyContent: "center",
        spacing: 1.5,
        sx: { color: gray[400], p: 5, textAlign: "center" },
        children: [
          /* @__PURE__ */ jsx(PersonOutlineIcon, { sx: { fontSize: 40 } }),
          /* @__PURE__ */ jsx(Typography, { variant: "body2", fontWeight: 600, children: t("선택된 임직원이 없습니다") }),
          /* @__PURE__ */ jsx(Typography, { variant: "caption", children: isMobile ? t("위 목록에서 임직원을 선택하세요") : t("왼쪽 목록에서 임직원을 선택하세요") })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs(
    Stack,
    {
      flex: "1 1 auto",
      sx: { overflowY: "auto", minHeight: 0, minWidth: 0 },
      px: 0.5,
      children: [
        /* @__PURE__ */ jsxs(Stack, { direction: "row", spacing: 2, alignItems: "center", pb: 2.5, children: [
          /* @__PURE__ */ jsx(UserAvatar, { name: user.preferredName, imageUrl, size: 64 }),
          /* @__PURE__ */ jsxs(Stack, { spacing: 0.5, minWidth: 0, children: [
            /* @__PURE__ */ jsxs(Stack, { direction: "row", spacing: 1.25, alignItems: "center", children: [
              /* @__PURE__ */ jsx(Typography, { variant: "subtitle3", fontWeight: 700, noWrap: true, children: user.preferredName || "-" }),
              user.employmentStatus && /* @__PURE__ */ jsx(StatusBadge, { status: user.employmentStatus })
            ] }),
            /* @__PURE__ */ jsx(
              OrgBreadcrumb,
              {
                user,
                getOrgGroupHref,
                onSelectOrgGroup
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsxs(
          Stack,
          {
            direction: { mobile: "column", laptop: "row" },
            sx: {
              borderTop: `1px solid ${gray[50]}`,
              borderBottom: `1px solid ${gray[50]}`
            },
            children: [
              /* @__PURE__ */ jsxs(DetailSection, { icon: /* @__PURE__ */ jsx(BusinessCenterOutlinedIcon, {}), title: t("직무"), children: [
                /* @__PURE__ */ jsx(DetailRow, { label: t("직위"), value: user.jobPosition }),
                /* @__PURE__ */ jsx(DetailRow, { label: t("직책"), value: user.jobTitle }),
                /* @__PURE__ */ jsx(DetailRow, { label: t("담당 업무"), value: user.workAssignment }),
                /* @__PURE__ */ jsx(DetailRow, { label: t("근무처"), value: user.workplace })
              ] }),
              /* @__PURE__ */ jsxs(
                DetailSection,
                {
                  icon: /* @__PURE__ */ jsx(PermContactCalendarOutlinedIcon, {}),
                  title: t("연락처"),
                  divided: true,
                  children: [
                    /* @__PURE__ */ jsx(
                      DetailRow,
                      {
                        label: t("이메일"),
                        value: user.email,
                        onCopy: () => handleCopy(user.email)
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      DetailRow,
                      {
                        label: t("휴대폰번호"),
                        value: user.phoneNumber,
                        onCopy: () => handleCopy(user.phoneNumber)
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      DetailRow,
                      {
                        label: t("내선번호"),
                        value: user.officePhone,
                        onCopy: () => handleCopy(user.officePhone)
                      }
                    )
                  ]
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx(Box, { sx: { pt: 2, pb: 1 }, children: /* @__PURE__ */ jsx(
          MemberSeatCard,
          {
            memberGuid: user.id,
            memberName: user.preferredName || ""
          }
        ) })
      ]
    }
  );
}
function StatusBadge({ status }) {
  const { green } = useColors();
  return /* @__PURE__ */ jsxs(
    Stack,
    {
      direction: "row",
      alignItems: "center",
      spacing: 0.75,
      sx: {
        height: 23,
        px: 1,
        borderRadius: 999,
        bgcolor: green[50],
        color: green[700]
      },
      children: [
        /* @__PURE__ */ jsx(
          Box,
          {
            sx: {
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: green[500]
            }
          }
        ),
        /* @__PURE__ */ jsx(Typography, { variant: "small", fontWeight: 600, children: status })
      ]
    }
  );
}
function DetailSection(props) {
  const { icon, title, children, divided = false } = props;
  const { gray } = useColors();
  return /* @__PURE__ */ jsxs(
    Box,
    {
      sx: {
        flex: "1 1 0",
        minWidth: 0,
        py: 2,
        ...divided ? {
          borderTop: { mobile: `1px solid ${gray[50]}`, laptop: "none" },
          borderLeft: { mobile: "none", laptop: `1px solid ${gray[50]}` },
          pl: { mobile: 0, laptop: 3 }
        } : { pr: { mobile: 0, laptop: 3 } }
      },
      children: [
        /* @__PURE__ */ jsx(SectionHeading, { icon, title }),
        children
      ]
    }
  );
}
function DetailRow(props) {
  const { label, value, onCopy } = props;
  const { t } = useTranslation("ui");
  const { gray } = useColors();
  return /* @__PURE__ */ jsxs(Stack, { direction: "row", alignItems: "center", sx: { minHeight: 34 }, children: [
    /* @__PURE__ */ jsx(Typography, { variant: "caption", color: gray[400], sx: { flex: "0 0 80px" }, children: label }),
    /* @__PURE__ */ jsxs(
      Stack,
      {
        direction: "row",
        alignItems: "center",
        spacing: 0.5,
        minWidth: 0,
        flex: "1 1 auto",
        children: [
          /* @__PURE__ */ jsx(
            Typography,
            {
              variant: "body2",
              fontWeight: 500,
              color: value ? gray[800] : gray[300],
              noWrap: true,
              children: value || EMPTY_VALUE
            }
          ),
          value && onCopy && /* @__PURE__ */ jsx(Tooltip, { title: t("복사"), placement: "top", children: /* @__PURE__ */ jsx(IconButton, { size: "small", onClick: onCopy, "aria-label": "copy", children: /* @__PURE__ */ jsx(ContentCopyIcon, { sx: { fontSize: 14 } }) }) })
        ]
      }
    )
  ] });
}
export {
  UserSearchDetail
};
